import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaCog, FaDatabase, FaPlus, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { FiRefreshCw } from 'react-icons/fi';
import PageHeader from 'Utils/PageHeader';
import { deleteDriverType, getDriverCatalog, listDrivers } from './api';
import UploadDriverModal from './UploadDriverModal';
import ConfigureDriverModal from './ConfigureDriverModal';
import DriverLogo from './DriverLogo';

/**
 * One row per supported database: every catalog flavor (installed or not) plus any
 * installed drivers with no catalog flavor (bundled PostgreSQL/MariaDB, customs).
 * Installed rows keep the full driver detail; not-installed rows carry the catalog
 * entry so the Upload action can open pre-picked. Installed rows sort first.
 */
/**
 * Non-JDBC engines whose drivers ship INSIDE the services (native protocol, not a
 * loadable jar): there is nothing to upload, so they appear as informational bundled
 * rows rather than catalog flavors. An upload flow for these would be a lie — no code
 * path consumes an uploaded mongo/dynamo jar; discover and masking use the bundled
 * MongoDB Java Driver / AWS SDK directly.
 */
const NATIVE_DRIVER_ROWS = [
  {
    key: 'native-mongodb',
    native: true,
    displayName: 'MongoDB Java Driver',
    engine: 'mongodb',
    maskingAvailable: true,
  },
  {
    key: 'native-dynamodb',
    native: true,
    displayName: 'AWS SDK for DynamoDB',
    engine: 'dynamodb',
    maskingAvailable: false, // discover/profile only — dynamo masking isn't shipped
  },
];

const mergeCatalogAndDrivers = (catalog, drivers) => {
  const byFlavor = new Map();
  (drivers || []).forEach((d) => {
    if (d && d.flavorId) byFlavor.set(d.flavorId, d);
  });
  const rows = [];
  const claimed = new Set();
  (catalog || []).forEach((entry) => {
    const installed = byFlavor.get(entry.flavorId) || null;
    if (installed) claimed.add(installed);
    rows.push({
      key: `flavor-${entry.flavorId}`,
      catalogEntry: entry,
      driver: installed,
      displayName: (installed && (installed.displayName || installed.name)) || entry.displayName,
      engine: (installed && installed.engine) || entry.engine,
    });
  });
  // Installed drivers with no catalog flavor: bundled (PostgreSQL/MariaDB) and customs.
  (drivers || []).forEach((d) => {
    if (claimed.has(d)) return;
    rows.push({
      key: `driver-${d.id || d.typeName || d.name}`,
      catalogEntry: null,
      driver: d,
      displayName: d.displayName || d.name || d.typeName || '-',
      engine: d.engine || d.database || '',
    });
  });
  rows.push(...NATIVE_DRIVER_ROWS);
  return rows.sort((a, b) => {
    // Native (bundled-in-service) rows sort with installed drivers — they ARE available.
    const rank = (r) => (r.driver || r.native ? 0 : 1);
    const installedDelta = rank(a) - rank(b);
    if (installedDelta !== 0) return installedDelta;
    return (a.displayName || '').localeCompare(b.displayName || '');
  });
};

const Drivers = ({ isAdmin }) => {
  const [drivers, setDrivers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload target: a catalog entry object, the string 'CUSTOM', or null (closed).
  const [uploadFlavor, setUploadFlavor] = useState(null);
  const [configureRow, setConfigureRow] = useState(null);

  const rows = useMemo(() => mergeCatalogAndDrivers(catalog, drivers), [catalog, drivers]);

  const loadDrivers = async () => {
    setFetchingData(true);
    setError('');
    try {
      const [driverRows, catalogRows] = await Promise.all([
        listDrivers(),
        getDriverCatalog().catch(() => []),
      ]);
      setDrivers(driverRows);
      setCatalog(catalogRows);
    } catch (e) {
      setError(e?.message || 'Failed to load drivers.');
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, []);

  const handleDelete = async (driver) => {
    const typeName = driver.typeName || driver.driverId;
    if (!typeName) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete driver type "${typeName}"? The jar is removed if no other type references it.`)) {
      return;
    }
    setError('');
    setSuccess('');
    try {
      await deleteDriverType(typeName);
      setSuccess(`Deleted driver type ${typeName}.`);
      await loadDrivers();
    } catch (e) {
      setError(e?.message || 'Delete failed.');
    }
  };

  const onAddSuccess = async () => {
    setSuccess('Driver added.');
    await loadDrivers();
  };
  const onConfigureSuccess = async () => {
    setSuccess('Driver updated.');
    await loadDrivers();
  };

  const colCount = isAdmin ? 8 : 7;

  return (
    <Col className='p-0'>
      <Container fluid>
        <Container>
          <PageHeader
            header='JDBC Drivers'
            icon={FaDatabase}
            tooltip='Supported databases and their JDBC drivers. Upload a jar to enable a database; PostgreSQL and MariaDB ship bundled.'
          />

          {error && <Alert variant='danger'>{error}</Alert>}
          {success && <Alert variant='success'>{success}</Alert>}

          <Row className='mb-3'>
            <Col className='d-flex justify-content-end'>
              {isAdmin && (
                <Button
                  variant='primary'
                  className='mr-2'
                  onClick={() => setUploadFlavor('CUSTOM')}
                >
                  <FaPlus className='mr-2' />Custom Driver
                </Button>
              )}
              <Button
                variant='outline-secondary'
                onClick={loadDrivers}
                disabled={fetchingData}
              >
                {fetchingData
                  ? <Spinner as='span' animation='border' size='sm' className='mr-2' />
                  : <FiRefreshCw className='mr-2' />}
                REFRESH
              </Button>
            </Col>
          </Row>

          <Card>
            <Card.Body className='p-0'>
              <Table striped hover responsive className='mb-0'>
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    <th>Version</th>
                    <th>Database</th>
                    <th>Driver Class</th>
                    <th>Status</th>
                    <th>Masking</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {fetchingData ? (
                    <tr>
                      <td colSpan={colCount} className='text-center p-4'>
                        <Spinner animation='border' variant='primary' />
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className='text-center p-4 text-muted'>
                        No drivers found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const { driver, catalogEntry } = row;
                      if (row.native) {
                        // Bundled native-protocol driver: nothing to upload, configure, or delete.
                        return (
                          <tr key={row.key}>
                            <td style={{ width: 32 }}>
                              <DriverLogo engine={row.engine} />
                            </td>
                            <td>{row.displayName} <span className='text-muted small'>(bundled)</span></td>
                            <td className='text-muted'>-</td>
                            <td>{row.engine}</td>
                            <td className='text-muted'>native protocol — no jar</td>
                            <td><Badge variant='success'>Available</Badge></td>
                            <td>
                              {row.maskingAvailable
                                ? <Badge variant='success'>Available</Badge>
                                : <Badge variant='secondary'>Unavailable</Badge>}
                            </td>
                            {isAdmin && <td className='text-muted'>-</td>}
                          </tr>
                        );
                      }
                      if (!driver) {
                        // Catalog-only row: a supported database with no jar installed yet.
                        return (
                          <tr key={row.key}>
                            <td style={{ width: 32 }}>
                              <DriverLogo engine={catalogEntry.engine} flavorId={catalogEntry.flavorId} />
                            </td>
                            <td className='text-muted'>{catalogEntry.displayName}</td>
                            <td className='text-muted'>-</td>
                            <td className='text-muted'>{catalogEntry.engine || '-'}</td>
                            <td className='text-muted'>{catalogEntry.driverClass || '-'}</td>
                            <td><Badge variant='secondary'>Not installed</Badge></td>
                            <td className='text-muted'>-</td>
                            {isAdmin && (
                              <td>
                                <Button
                                  size='sm'
                                  variant='outline-primary'
                                  onClick={() => setUploadFlavor(catalogEntry)}
                                  title={`Upload the ${catalogEntry.displayName} driver jar`}
                                >
                                  <FaUpload className='mr-1' />Upload
                                </Button>
                              </td>
                            )}
                          </tr>
                        );
                      }
                      const name = driver.displayName || driver.name || driver.typeName || '-';
                      const jarSuffix = driver.jarFileName ? ` (${driver.jarFileName})` : '';
                      const available = driver.available !== false;
                      return (
                        <tr key={row.key}>
                          <td style={{ width: 32 }}>
                            <DriverLogo engine={driver.engine || driver.database} flavorId={driver.flavorId} />
                          </td>
                          <td>{name}{jarSuffix}</td>
                          <td>{driver.driverVersion || driver.version || '-'}</td>
                          <td>{driver.engine || driver.database || '-'}</td>
                          <td>{driver.className || driver.driverClass || '-'}</td>
                          <td>
                            {available
                              ? <Badge variant='success'>Available</Badge>
                              : <Badge variant='secondary'>Unavailable</Badge>}
                          </td>
                          <td>
                            {driver.maskingAvailable
                              ? <Badge variant='success'>Available</Badge>
                              : <Badge variant='secondary'>Unavailable</Badge>}
                            {driver.maskingLastError && (
                              <div className='small text-muted'>{driver.maskingLastError}</div>
                            )}
                          </td>
                          {isAdmin && (
                            <td>
                              <Button
                                size='sm'
                                variant='outline-primary'
                                className='mr-1'
                                onClick={() => setConfigureRow(driver)}
                                title='Configure'
                              >
                                <FaCog />
                              </Button>
                              {!driver.bundled && (
                                <Button
                                  size='sm'
                                  variant='outline-danger'
                                  onClick={() => handleDelete(driver)}
                                  title='Delete'
                                >
                                  <FaTrashAlt />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Container>
      </Container>

      <UploadDriverModal
        show={!!uploadFlavor}
        flavor={uploadFlavor}
        onHide={() => setUploadFlavor(null)}
        onSuccess={onAddSuccess}
      />
      <ConfigureDriverModal
        show={!!configureRow}
        driver={configureRow}
        onHide={() => setConfigureRow(null)}
        onSuccess={onConfigureSuccess}
      />
    </Col>
  );
};

export default Drivers;
